import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import AppointmentPicker from "../components/AppointmentPicker";
import BackLink, { parentOf } from "../components/BackLink";
import Breadcrumbs from "../components/Breadcrumbs";
import HeroVehicle from "../components/HeroVehicle";
import InquiryForm from "../components/InquiryForm";
import SiteShell from "../components/SiteShell";
import {
  appraisalPath,
  bookServicePath,
  business,
  businessLanes,
  principles,
  qualityAssurance,
  seo,
  seoKeywords,
  serviceArea,
  serviceCategories,
  serviceSteps,
  sourcingSteps,
  tireQuotePath,
  socialProfiles,
  tireValues,
  vehicleRequestPath,
} from "../siteData";

const origin = "https://primepathmotors.com";
const defaultShareImage = "/share-card.png";

function Meta({ meta, path = "/" }) {
  const url = `${origin}${path}`;
  const image = `${origin}${meta.image || defaultShareImage}`;
  // A search result and a link unfurl have different budgets, so they get
  // different strings. og:site_name already prints the brand.
  const social = meta.social || { title: meta.title, description: meta.description };
  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="keywords" content={seoKeywords} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={social.title} />
      <meta property="og:description" content={social.description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="Prime Path Motors" />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Prime Path Motors, Drive Your Prime Path" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={social.title} />
      <meta name="twitter:description" content={social.description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
}

function PageHero({ eyebrow, title, copy, path, back = false, children }) {
  return (
    <section className="page-hero">
      {path ? <Breadcrumbs path={path} /> : null}
      {back && path ? <BackLink path={path} /> : null}
      <div className="hero-copy">
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {copy ? <p className="lede">{copy}</p> : null}
        {children}
      </div>
      <span className="path-rule" aria-hidden="true" />
    </section>
  );
}

function StepBand({ steps, eyebrow, title }) {
  return (
    <section className="section dark-section">
      <div className="section-heading split-heading">
        <p className="eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
      </div>
      <ol className="process-list">
        {steps.map(([number, name, copy]) => (
          <li key={number}>
            <span>{number}</span>
            <h3>{name}</h3>
            <p>{copy}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

// Machine-readable identity for search. Address, phone and hours come from the
// same siteData the footer renders, so the two can never drift apart.
function OrganizationSchema() {
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "AutoDealer",
        name: business.name,
        slogan: business.tagline,
        url: origin,
        logo: `${origin}/favicon-512.png`,
        image: `${origin}${defaultShareImage}`,
        telephone: `+1-469-960-3446`,
        email: business.email,
        address: {
          "@type": "PostalAddress",
          streetAddress: business.addressLine1,
          addressLocality: "Alvarado",
          addressRegion: "TX",
          postalCode: "76009",
          addressCountry: "US",
        },
        areaServed: ["Alvarado", "Cleburne", "Burleson", "Mansfield", "Crowley", "Arlington", "Fort Worth"]
          .map((name) => ({ "@type": "City", name })),
        openingHoursSpecification: [
          { days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "09:00", closes: "18:00" },
          { days: ["Saturday"], opens: "10:00", closes: "16:00" },
        ].map(({ days, opens, closes }) => ({
          "@type": "OpeningHoursSpecification", dayOfWeek: days, opens, closes,
        })),
        // sameAs is how a search engine ties the site to the profiles and
        // treats them as one business rather than several.
        sameAs: socialProfiles.map((s) => s.url),
        department: [
          { "@type": "AutoRepair", name: "Prime Path Motors Service", url: `${origin}/auto-services` },
          { "@type": "TireShop", name: "Prime Path Motors Tires", url: `${origin}/tires` },
        ],
      })}</script>
    </Helmet>
  );
}

export function HomePage() {
  return (
    <SiteShell>
      <Meta meta={seo.home} path="/" />
      <OrganizationSchema />

      {/*
        Instrument cluster hero.

        The plate was measured, not eyeballed: the top 32.8 percent of
        ppm-hero-approach.jpg is pure black, zero lit pixels, and the vehicle
        begins at y=354 of 1080. Every word here sits inside that band, which is
        why nothing is laid over the car any more. The scrim was carrying the
        headline's contrast before; on a band that is already black it can be
        much lighter, so the car reads brighter than it did.

        The three lanes are a strip rather than three stacked buttons because
        vehicles, service and tires are three equal businesses, not one offer
        with two afterthoughts. Each carries a data-cursor label for the ring.
      */}
      <section className="home-hero">
        <HeroVehicle />

        <div className="cluster-corners">
          <p><b>Alvarado, Texas</b></p>
          <p className="cluster-right"><b>Open by appointment</b></p>
        </div>

        <div className="home-hero-copy">
          <h1>Drive Your <span>Prime Path</span></h1>
          <p className="cluster-statement">Sourced to spec &middot; Delivered nationwide &middot; Serviced in Alvarado</p>
        </div>

        {/* Every hero CTA is an action and opens the request it names. The
            approved pack said "Find Your Vehicle" and "Shop Tires", but those
            are nav labels: they promise browsing, they landed the visitor on an
            explainer, and a second click was needed to reach the request.
            "Shop Tires" also promised tire shopping this business does not
            offer. Content pack needs a V2.1 to match. */}
        {/* No numerals here. The three-door band directly below already numbers
            these same three lanes 01 to 03, and on a phone the two sit back to
            back, which read as a mistake rather than a rhythm. */}
        <nav className="cluster-strip" aria-label="What Prime Path Motors can do">
          <Link to={vehicleRequestPath} data-cursor="Start">
            <span>Vehicles</span><b>Start Your Search<i aria-hidden="true">&rarr;</i></b>
          </Link>
          <Link to={bookServicePath} data-cursor="Book">
            <span>Service</span><b>Book Service<i aria-hidden="true">&rarr;</i></b>
          </Link>
          <Link to={tireQuotePath} data-cursor="Quote">
            <span>Tires</span><b>Get a Tire Quote<i aria-hidden="true">&rarr;</i></b>
          </Link>
        </nav>
      </section>

      {/* V2 section 2: three-door band, one card per business lane */}
      <section className="business-lanes" aria-label="Ways Prime Path Motors can help">
        {businessLanes.map((lane) => (
          <Link key={lane.number} to={lane.to}>
            <span>{lane.number}</span>
            <h2>{lane.title}</h2>
            <p>{lane.copy}</p>
            <b>{lane.cta} ↗</b>
          </Link>
        ))}
      </section>

      {/* V2 section 2: Where Excellence Meets Automotive */}
      <section className="section story-section">
        <div>
          <p className="eyebrow">Built around the request</p>
          <h2>Where Excellence<br />Meets Automotive</h2>
        </div>
        <div className="story-copy">
          <p>At Prime Path Motors, we have redefined the pre-owned vehicle buying experience. Based in Alvarado, Texas, we deliver personalized service with nationwide reach and modern technology.</p>
          <p>Every vehicle in our curated selection undergoes comprehensive inspection and reconditioning. We source premium vehicles that meet our strict quality standards, whether from our current selection or specifically acquired to match your requirements.</p>
          <p>Our process is built on three principles: transparency in every transaction, quality you can trust, and service that puts your needs first. From initial inquiry through delivery and beyond, we are committed to making your experience exceptional.</p>
          <Link className="text-link" to="/about">How we work <span>↗</span></Link>
        </div>
      </section>

      <StepBand steps={sourcingSteps} eyebrow="Our process" title="A clear path from request to handoff" />

      {/* V2 section 2: tire feature band */}
      <section className="feature-band cream-band">
        <div>
          <p className="eyebrow">Tires</p>
          <h2>The Right Tires,<br />Without the Runaround</h2>
        </div>
        <div>
          <p>Tires are priced like a commodity and sold like a mystery. We do it the way we do everything: tell us your vehicle and your driving, and we quote you the right tire options with clear pricing. Passenger, truck, and SUV fitments across major brands. Professional installation can be arranged.</p>
          <Link className="button button-dark" to={tireQuotePath}>Get a Tire Quote</Link>
        </div>
      </section>

      {/* V2 section 6: trade-in band, in the space the two financial tools occupied */}
      <section className="feature-band appraisal-band">
        <div>
          <p className="eyebrow">Trade-ins</p>
          <h2>What&rsquo;s Your<br />Trade Worth?</h2>
        </div>
        <div>
          <p>Tell us what you are driving now. We look at the actual vehicle, its mileage, and its condition, and we come back with a number we can stand behind. If you are sourcing a vehicle with us, your trade gets worked into the deal.</p>
          <Link className="button button-outline" to="/appraisal">Get Your Trade Appraisal</Link>
        </div>
      </section>

      {/* V2 section 2: the "Can't Find What You're Looking For?" band, kept */}
      <section className="closing-cta">
        <p className="eyebrow">Nationwide sourcing</p>
        <h2>Can&rsquo;t Find What You&rsquo;re Looking For?</h2>
        <p className="lede">Tell us what you need. We will source it, inspect it, and deliver it.</p>
        <ul className="closing-points">
          <li>Nationwide vehicle sourcing</li>
          <li>Thorough inspection process</li>
          <li>Professional delivery</li>
        </ul>
        <div className="button-row">
          <Link className="button button-gold" to={vehicleRequestPath}>Tell Us What You Need</Link>
          <a className="button button-outline" href={`tel:${business.phoneHref}`}>Call {business.phoneDisplay}</a>
        </div>
      </section>
    </SiteShell>
  );
}

export function VehiclePage() {
  return (
    <SiteShell>
      <Meta meta={seo.vehicle} path="/find-your-vehicle" />
      <PageHero
        eyebrow="Nationwide vehicle sourcing"
        title="Find Your Vehicle"
        copy="You tell us the vehicle. We handle everything else."
        path="/find-your-vehicle"
      >
        <p className="hero-body">Submit your preferences, budget, and requirements. Our team searches our nationwide network, inspects every candidate against our standards, and brings you complete details, inspection reports, and clear pricing before you decide. Then we handle the paperwork and deliver directly to your location.</p>
        <Link className="button button-gold" to={vehicleRequestPath}>Start Your Search</Link>
      </PageHero>

      {/* V2 section 3: the reassurance the financing page used to carry */}
      <section className="section brief-section">
        <div>
          <p className="eyebrow">How pricing works here</p>
          <h2>Your budget<br />is the brief</h2>
        </div>
        <div className="story-copy">
          <p>You set the number before we start looking, and we source against it. You see the full cost of the vehicle in writing before you commit to anything. If we cannot find the right vehicle at your number, we tell you that instead of talking you up to a bigger one.</p>
        </div>
      </section>

      <StepBand steps={sourcingSteps} eyebrow="Our process" title="A clear path from request to handoff" />

      <section className="closing-cta">
        <p className="eyebrow">Ready when you are</p>
        <h2>Tell us what you need.</h2>
        <p className="lede">It takes a couple of minutes, and it is how every search here starts.</p>
        <div className="button-row">
          <Link className="button button-gold" to={vehicleRequestPath}>Start Your Search</Link>
          <a className="button button-outline" href={`tel:${business.phoneHref}`}>Call {business.phoneDisplay}</a>
        </div>
      </section>
    </SiteShell>
  );
}

// The request pages. Each one is the destination of a CTA that names it, so the
// form is the first thing on the page rather than something to scroll toward.
export function VehicleRequestPage() {
  return (
    <SiteShell>
      <Meta meta={seo.vehicleRequest} path={vehicleRequestPath} />
      <PageHero
        eyebrow="Start here"
        title="Tell Us What You Need"
        copy="Your budget is the brief. Set the number, tell us what you will not compromise on, and we source against it."
        path={vehicleRequestPath}
        back
      />
      <div className="section form-section">
        <InquiryForm
          type="vehicle"
          title="Tell Us What You Need"
          showIntro={false}
          submitLabel="Send vehicle request"
          cancelTo={parentOf(vehicleRequestPath).to}
          cancelLabel="Cancel"
        />
      </div>
    </SiteShell>
  );
}

export function TireQuotePage() {
  return (
    <SiteShell>
      <Meta meta={seo.tireQuote} path={tireQuotePath} />
      <PageHero
        eyebrow="Start here"
        title="Get Your Tire Quote"
        copy="Tell us the vehicle and how you drive it. We come back with options and pricing. No obligation, no runaround."
        path={tireQuotePath}
        back
      />
      <div className="section form-section">
        <InquiryForm
          type="tire"
          title="Get Your Tire Quote"
          showIntro={false}
          submitLabel="Send tire request"
          cancelTo={parentOf(tireQuotePath).to}
          cancelLabel="Cancel"
        />
      </div>
    </SiteShell>
  );
}

export function BookServicePage() {
  const [slot, setSlot] = useState({ date: "", time: "" });

  return (
    <SiteShell>
      <Meta meta={seo.bookService} path={bookServicePath} />
      <PageHero
        eyebrow="Start here"
        title="Book Your Appointment"
        copy="Pick a date and time, tell us what the vehicle is doing, and we confirm with you before anything is on the board."
        path={bookServicePath}
        back
      />
      <div className="section form-section">
        <InquiryForm
          type="service"
          title="Book Your Appointment"
          showIntro={false}
          submitLabel="Request this appointment"
          cancelTo={parentOf(bookServicePath).to}
          cancelLabel="Cancel"
          successMessage="Your appointment request was received. We will confirm the date and time by phone or email before it is on the board."
          lead={<AppointmentPicker value={slot} onChange={setSlot} />}
          validateLead={() => {
            if (!slot.date) return "Choose a date for your appointment.";
            if (!slot.time) return "Choose a time for your appointment.";
            return null;
          }}
          onSuccess={() => setSlot({ date: "", time: "" })}
        />
      </div>
    </SiteShell>
  );
}

export function ServicesPage() {
  return (
    <SiteShell>
      <Meta meta={seo.services} path="/auto-services" />
      <PageHero
        eyebrow="Auto services"
        title="Service You Can See"
        copy="Automotive service by appointment in Alvarado, Texas. Written estimates before any work begins. Photos of what we find. Your old parts back on request."
        path="/auto-services"
      >
        <Link className="button button-gold" to={bookServicePath}>Book Service</Link>
      </PageHero>

      <StepBand steps={serviceSteps} eyebrow="How service works" title="From concern to confirmed visit" />

      <section className="section cream-section">
        <div className="section-heading">
          <p className="eyebrow">What we service</p>
          <h2>Common reasons to bring the vehicle in</h2>
        </div>
        <div className="editorial-grid">
          {serviceCategories.map(([number, name, copy]) => (
            <article key={number}>
              <span>{number}</span>
              <h3>{name}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
        <p className="grid-note">
          Need something not listed? Call us at <a href={`tel:${business.phoneHref}`}>{business.phoneDisplay}</a>. If it is outside what we do, we will point you to someone trustworthy who does it.
        </p>
      </section>

      <section className="service-area">
        <p className="eyebrow">Service area</p>
        <h2>Based in Alvarado. Built for the greater Fort Worth driver.</h2>
        <p>{serviceArea}</p>
      </section>

      <section className="closing-cta">
        <p className="eyebrow">Appointments</p>
        <h2>Pick a date and a time.</h2>
        <p className="lede">Choose the slot that works, tell us what the vehicle is doing, and we confirm it with you.</p>
        <div className="button-row">
          <Link className="button button-gold" to={bookServicePath}>Book Service</Link>
          <a className="button button-outline" href={`tel:${business.phoneHref}`}>Call {business.phoneDisplay}</a>
        </div>
      </section>
    </SiteShell>
  );
}

export function TiresPage() {
  return (
    <SiteShell>
      <Meta meta={seo.tires} path="/tires" />
      <PageHero
        eyebrow="Tires"
        title="Tires, Done the Prime Path Way"
        copy="Tell us what you drive and how you drive it. We quote the right tires at a clear price."
        path="/tires"
      >
        <Link className="button button-gold" to={tireQuotePath}>Get a Tire Quote</Link>
      </PageHero>

      <section className="section principles-section">
        <div>
          <p className="eyebrow">What guides the quote</p>
          <h2>Fit. Use.<br />Priority.</h2>
        </div>
        <div className="principles-list">
          {tireValues.map(([number, name, copy]) => (
            <article key={number}>
              <span>{number}</span>
              <h3>{name}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="closing-cta">
        <p className="eyebrow">Tire quotes</p>
        <h2>Tell us what you drive.</h2>
        <p className="lede">We come back with good, better and best, priced for your exact fitment.</p>
        <div className="button-row">
          <Link className="button button-gold" to={tireQuotePath}>Get a Tire Quote</Link>
          <a className="button button-outline" href={`tel:${business.phoneHref}`}>Call {business.phoneDisplay}</a>
        </div>
      </section>

      <section className="cross-sell">
        <p>While your wheels are on our minds: brakes, suspension, and alignment-related wear all show up in your tires. If your tires are wearing unevenly, book a service appointment and we will find out why.</p>
        <Link className="text-link" to={bookServicePath}>Book Service <span>↗</span></Link>
      </section>
    </SiteShell>
  );
}

export function AppraisalPage() {
  return (
    <SiteShell>
      <Meta meta={seo.appraisal} path={appraisalPath} />
      <PageHero
        eyebrow="Trade-ins"
        title="What&rsquo;s Your Trade Worth?"
        copy="Tell us what you are driving now. We look at the actual vehicle, its mileage, and its condition, and we come back with a number we can stand behind. If you are sourcing a vehicle with us, your trade gets worked into the deal."
        path={appraisalPath}
        back
      />
      <div className="section form-section">
        <InquiryForm
          type="appraisal"
          title="Get Your Trade Appraisal"
          submitLabel="Send appraisal request"
          cancelTo={parentOf(appraisalPath).to}
          cancelLabel="Cancel"
        />
      </div>
    </SiteShell>
  );
}

export function AboutPage() {
  return (
    <SiteShell>
      <Meta meta={seo.about} path="/about" />
      <PageHero eyebrow="Licensed Texas independent dealer" title="The Prime Path Difference" copy="Excellence in every detail." path="/about" />

      <section className="section story-section">
        <div>
          <p className="eyebrow">The operating idea</p>
          <h2>Served,<br />not sold.</h2>
        </div>
        <div className="story-copy">
          <p>Prime Path Motors is a licensed Texas independent dealer based in Alvarado, built on a simple idea: buying a vehicle should feel like being served, not sold.</p>
          <p>Instead of pushing whatever happens to be on a lot, we start with what you actually want. Our team sources vehicles through a nationwide network, holds every one to strict inspection and reconditioning standards, and delivers anywhere in the country. And because vehicles need care after the purchase, we back the buying experience with appointment-based automotive service and tire sales at our Alvarado location.</p>
        </div>
      </section>

      <section className="section cream-section">
        <div className="section-heading">
          <p className="eyebrow">Three principles</p>
          <h2>Excellence in every detail.</h2>
        </div>
        <div className="editorial-grid">
          {principles.map(([number, name, copy]) => (
            <article key={number}>
              <span>{number}</span>
              <h3>{name}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <StepBand steps={qualityAssurance} eyebrow="Quality assurance" title="Every vehicle, through every handoff" />
    </SiteShell>
  );
}

export function ContactPage() {
  return (
    <SiteShell>
      <Meta meta={seo.contact} path="/contact" />
      <PageHero eyebrow="Contact Prime Path Motors" title="Talk to Us" copy="Need service? The fastest path is booking directly." path="/contact">
        <Link className="button button-gold" to={bookServicePath}>Book Service</Link>
      </PageHero>

      <section className="contact-details">
        <div>
          <p className="eyebrow">Visit</p>
          <address>{business.addressLine1}<br />{business.addressLine2}</address>
        </div>
        <div>
          <p className="eyebrow">Call or email</p>
          <a href={`tel:${business.phoneHref}`}>{business.phoneDisplay}</a>
          <a href={`mailto:${business.email}`}>{business.email}</a>
        </div>
        <div>
          <p className="eyebrow">Hours</p>
          {business.hours.map((line) => <p key={line}>{line}</p>)}
        </div>
      </section>

      <div className="section form-section">
        <InquiryForm
          type="general"
          title="Everything else"
          intro="Use the form or reach us directly. Tell us what you are after and the right person picks it up."
        />
      </div>
    </SiteShell>
  );
}

export function LegalPage({ type }) {
  const privacy = type === "privacy";
  return (
    <SiteShell>
      <Meta meta={privacy ? seo.privacy : seo.terms} path={`/${type}`} />
      <section className="legal-page">
        <Breadcrumbs path={`/${type}`} />
        <p className="eyebrow">Prime Path Motors</p>
        <h1>{privacy ? "Privacy notice" : "Website terms"}</h1>
        {privacy ? (
          <>
            <p>Information you choose to submit through this website is used to respond to your request and coordinate the related vehicle, service, tire, appraisal, or general inquiry.</p>
            <p>Do not send payment-card data, passwords, or other sensitive credentials through a website form. For questions about information you submitted, contact <a href={`mailto:${business.email}`}>{business.email}</a>.</p>
          </>
        ) : (
          <>
            <p>Website information is provided for general communication and does not create a purchase, service, or appraisal commitment.</p>
            <p>Appointments, vehicle availability, scope, pricing, and other transaction details require direct confirmation from Prime Path Motors.</p>
          </>
        )}
      </section>
    </SiteShell>
  );
}
